import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import "./home.css";
import { HeroSection } from "../heroSection/HeroSection";
import Choose from "../../Components/Homecom/choose";
import { useFavorites } from "../../context/FavoritesContext";
import { ShoppingCart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { fetchHomeProducts } from "../../api/home.api";
import { fetchBrands } from "../../api/brands.api";
import { sortNewestFirst } from "../../utils/sortDisplayOrder";
import { sortBrandsForDisplay } from "../../utils/brandDisplayOrder";

const toFiniteNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeHomeProduct = (product = {}) => ({
  ...product,
  name: product.name ?? product.title ?? "",
  title: product.title ?? product.name ?? "",
  category_name: product.category_name ?? product.brand ?? "",
  price: toFiniteNumber(product.price ?? product.selling_price),
  cost_price: toFiniteNumber(product.cost_price ?? product.mrp),
  points: toFiniteNumber(product.points ?? product.Points),
  member_points: toFiniteNumber(
    product.member_points ?? product.memberPoints
  ),
});

const normalizeHomePreorderProduct = (product = {}) => ({
  ...product,
  id: product.id,
  name: product.name ?? product.title ?? "",
  title: product.title ?? product.name ?? "",
  category_name: product.category_name ?? product.brand ?? "Pre-Order",
  price: toFiniteNumber(product.deposit_amount ?? product.price),
  cost_price: 0,
  points: toFiniteNumber(product.points ?? product.Points),
  member_points: toFiniteNumber(
    product.member_points ?? product.memberPoints
  ),
  image: product.image ?? product.img ?? "",
  isPreorder: true,
});

const normalizeHomeComboProduct = (product = {}) => ({
  ...product,
  id: product.id,
  name: product.name ?? product.title ?? "",
  title: product.title ?? product.name ?? "",
  category_name: product.brand ?? product.category_name ?? "Combo Set",
  price: toFiniteNumber(
    product.price ?? product.selling_price ?? product.combo_price
  ),
  cost_price: toFiniteNumber(product.cost_price ?? product.mrp),
  points: toFiniteNumber(product.points ?? product.Points),
  member_points: toFiniteNumber(
    product.member_points ?? product.memberPoints
  ),
  image: product.image ?? product.img ?? "",
  isCombo: true,
});

const resolveDisplayPoints = (product, isMember) => {
  const basePoints = toFiniteNumber(product?.points ?? product?.Points);
  const memberPoints = toFiniteNumber(
    product?.member_points ?? product?.memberPoints
  );

  if (isMember) {
    return memberPoints;
  }

  return basePoints;
};

export default function DiecastSection() {
  const [comboProducts, setComboProducts] = useState([]);
  const [preorderProducts, setPreorderProducts] = useState([]);
  const [newArrivals, setNewArrivals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [brands, setBrands] = useState([]);

  const navigate = useNavigate();
  useEffect(() => {
    let mounted = true;

    fetchHomeProducts()
      .then((data) => {
        if (!mounted) return;
        setPreorderProducts(
          sortNewestFirst(
            (data.preorder_products || []).map(normalizeHomePreorderProduct)
          )
        );
        setComboProducts(
          sortNewestFirst(
            (data.combos || data.comboscoming || []).map(
              normalizeHomeComboProduct
            )
          )
        );
        setNewArrivals(
          sortNewestFirst((data.new_arrivals || []).map(normalizeHomeProduct))
        );
      })
      .catch(() => {
        if (!mounted) return;
        setComboProducts([]);
        setPreorderProducts([]);
        setNewArrivals([]);
      });

    return () => {
      mounted = false;
    };
  }, []);


  useEffect(() => {
    let mounted = true;

    fetchBrands()
      .then((data) => {
        if (!mounted) return;
        setBrands(sortBrandsForDisplay(Array.isArray(data) ? data : []));
      })
      .catch(() => {
        if (mounted) setBrands([]);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, []);


  if (loading) return <div className="page">
    <div className="loading-wrap">
      <div className="loader" />
      <div>Loading...</div>
    </div>
  </div>;
  return (
    <div id="home">
      <Helmet>
        <title>Buy Diecast Model Cars Online in India | 164 Diecast Zone</title>
        <meta
          name="description"
          content="Shop premium 1:64 diecast model cars in India — Mini GT, chase editions, combo deals, and event exclusives. Free membership, redeem rewards, fast shipping."
        />
        <link rel="canonical" href="https://164diecastzone.com/" />
        <meta property="og:title" content="Buy Diecast Model Cars Online in India | 164 Diecast Zone" />
        <meta
          property="og:description"
          content="Premium 1:64 diecast collections, exclusive drops, combo offers, memberships, and redeem rewards."
        />
        <meta property="og:url" content="https://164diecastzone.com/" />
      </Helmet>
      <HeroSection />
      <section className="diecast-section">
        <div className="section-inner">
          <header className="section-header">
            <h2>
              FAVOURITE <span className="accent">BRANDS</span>
            </h2>
          </header>

          <div className="home-brand-showcase">
            {brands.map((brand) => (
              <button
                key={brand.id}
                type="button"
                className="home-brand-card"
                onClick={() =>
                  navigate(`/category/${encodeURIComponent(brand.name)}`)
                }
              >
                <div className="home-brand-image-wrap">
                  <img
                    src={brand.image_small}
                    alt={brand.name}
                    className="home-brand-image"
                  />
                </div>
                <span className="home-brand-name">{brand.name}</span>
              </button>
            ))}
          </div>

          <Section
            title={{ main: "PRE", accent: "ORDER" }}
            products={preorderProducts}
            id="pre-order-products"
            isPreorder
            viewAllPath="/pre-order"
          />

          <Section
            title={{ main: "COMBO", accent: "SETS" }}
            products={comboProducts}
            id="combo-products"
            isCombo
            viewAllPath="/combos"
          />

          <Section
            title={{ main: "NEW", accent: "ARRIVALS" }}
            products={newArrivals}
            id="new-arrivals"
            viewAllPath="/category/ALL"
          />

        </div>
      </section>

      <Choose />
    </div>

  );
}


function Section({
  title,
  products,
  id,
  isPreorder = false,
  isCombo = false,
  viewAllPath = "",
}) {
  const ITEMS_PER_PAGE = 5;
  const [startIndex, setStartIndex] = useState(0);
  const navigate = useNavigate();
  const hasProducts = Array.isArray(products) && products.length > 0;

  useEffect(() => {
    setStartIndex(0);
  }, [products]);

  const handleNext = () => {
    if (startIndex + ITEMS_PER_PAGE < products.length) {
      setStartIndex(startIndex + ITEMS_PER_PAGE);
    }
  };

  const handlePrev = () => {
    if (startIndex - ITEMS_PER_PAGE >= 0) {
      setStartIndex(startIndex - ITEMS_PER_PAGE);
    }
  };

  const visibleProducts = products.slice(
    startIndex,
    startIndex + ITEMS_PER_PAGE
  );

  if (!hasProducts) {
    return null;
  }

  return (
    <div id={id} className="section-spacer">
      <div className="products-header1">
        <h3>
          {title.main} <span className="accent">{title.accent}</span>
        </h3>
        {viewAllPath ? (
          <button
            type="button"
            className="home-section-view-all"
            onClick={() => navigate(viewAllPath)}
          >
            View All
          </button>
        ) : null}
      </div>

      <div className="product-grid-wrapper">
        <button
          onClick={handlePrev}
          disabled={startIndex === 0}
          className="nav-btn prev">‹</button>

        <div className="product-grid1">
          {visibleProducts.map((p) => (
            <ProductCard key={p.id} p={p} isPreorder={isPreorder} isCombo={isCombo} />
          ))}
        </div>

        <button
          onClick={handleNext}
          disabled={startIndex + ITEMS_PER_PAGE >= products.length}
          className="nav-btn next">›</button>
      </div>
    </div>
  );
}


function ProductCard({ p, isPreorder = false, isCombo = false }) {
  const { toggleFavorite, isFavorite } = useFavorites();
  const {
    isMember,
    isAuthenticated,
    setShowLoginModal,
  } = useAuth();
  const {
    updateCart,
    getProductQuantity,
    getComboQuantity,
    getPreorderQuantity,
  } = useCart();

  const product = {
    ...(isPreorder
      ? normalizeHomePreorderProduct(p)
      : isCombo
        ? normalizeHomeComboProduct(p)
        : normalizeHomeProduct(p)),
    id: p.id,
    title: p.title ?? p.name,
    subtitle: p.subtitle ?? p.category_name,
    img: p.img ?? p.image,
  };
  const mrp = product.cost_price;
  const price = product.price;
  const displayPoints = resolveDisplayPoints(product, isMember);
  const showPointsBadge = displayPoints > 0;

  const discount =
    mrp && mrp > price
      ? Math.round(((mrp - price) / mrp) * 100)
      : 0;
  const targetPath = isPreorder
    ? `/pre-order/product/${product.id}`
    : isCombo
      ? `/combo/${product.id}`
      : `/category/${product.category_name}/product/${product.id}`;
  const cartQty = isPreorder
    ? getPreorderQuantity(product.id)
    : isCombo
      ? getComboQuantity(product.id)
      : getProductQuantity(product.id);
  const cartType = isPreorder ? "preorder" : isCombo ? "combo" : "product";
  const addButtonLabel = "Add To Cart";
  const showProductPrice = !(isPreorder && isMember);

  const handleAdd = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    updateCart({
      type: cartType,
      id: product.id,
      delta: 1,
    });
  };

  const handleDecrease = () => {
    updateCart({
      type: cartType,
      id: product.id,
      delta: -1,
    });
  };

  const handleIncrease = () => {
    if (!isAuthenticated) {
      setShowLoginModal(true);
      return;
    }

    updateCart({
      type: cartType,
      id: product.id,
      delta: 1,
    });
  };

  return (

    <div className="product-card1">
      <Link to={targetPath}>
        <div className="img-wrap1">
          <img src={product.img} alt={product.title} />
          {showPointsBadge && (
            <div className="points-badge">
              ₹{displayPoints} Rewards
            </div>
          )}
        </div>
      </Link>


      <div className="prod-info-home">
        <div className="prod-title-home">
          {product.title}
          <span className="prod-subtitle-home">{product.subtitle}</span>
        </div>
        {isMember && mrp != null && mrp > price && (
          <div className="related-mrp-row">
            <span className="related-mrp">₹{mrp}</span>
            {discount >= 10 && (
              <span className="product-discount">{discount}% OFF</span>
            )}
          </div>
        )}

        {showProductPrice && (
          <div className="price-row">
            <div className="price-home">₹{product.price}</div>
          </div>
        )}

        <div className="home-product-actions">
          {cartQty > 0 ? (
            <div className="home-quantity-selector">
              <button onClick={handleDecrease}>-</button>
              <span>{cartQty}</span>
              <button onClick={handleIncrease}>+</button>
            </div>
          ) : (
            <button
              type="button"
              className="home-add-to-cart-btn"
              onClick={handleAdd}
            >
              <ShoppingCart size={16} />
              {addButtonLabel}
            </button>
          )}

          {!isPreorder && !isCombo && (
            <button
              type="button"
              className={`home-card-fav ${isFavorite(product.id) ? "active" : ""}`}
              onClick={() => toggleFavorite(product.id)}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill={isFavorite(product.id) ? "currentColor" : "none"}
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m12 21-1.45-1.32C5.4 15.04 2 11.95 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.45-3.4 6.54-8.55 11.18Z" />
              </svg>
            </button>
          )}
        </div>

      </div>
    </div>
  );
}
